import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, ChevronRight, Receipt, XCircle, RefreshCw } from 'lucide-react';
import { useT } from '../context/LanguageContext';

export function PaymentSuccess() {
  const t = useT();
  const [params] = useSearchParams();
  const orderId = params.get('order_id') || params.get('subscription_id') || '';
  const paymentId = params.get('payment_id') || '';
  const amount = params.get('amount') || '';
  const currency = params.get('currency') || 'EUR';
  const label = params.get('label') || '';
  const product = params.get('product') || '';

  return (
    <div className="min-h-screen bg-[#FAF7F0] flex items-center justify-center p-6" data-testid="payment-success-page">
      <div className="w-full max-w-lg bg-white border border-[#D4AF37]/50 rounded-2xl px-8 py-9 text-center relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-32 h-32 bg-[#D4AF37]/10 rounded-full" aria-hidden="true" />
        <div className="relative">
          <div className="w-16 h-16 rounded-full bg-green-50 border border-green-200 flex items-center justify-center mx-auto mb-5">
            <CheckCircle2 className="w-9 h-9 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-[#1A2F4A] mb-2" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="success-title">
            {t({ fr: 'Merci !', en: 'Thank you!' })}
          </h1>
          <p className="text-[14px] text-[#6B4E37] mb-6">
            {t({ fr: 'Votre paiement a été confirmé avec succès.', en: 'Your payment has been successfully confirmed.' })}
          </p>

          <div className="bg-[#FAF7F0] border border-[#E8E4DC] rounded-xl px-5 py-4 text-left space-y-2 mb-6" data-testid="success-receipt">
            {label && <Row label={t({ fr: 'Produit', en: 'Product' })} value={label} />}
            {amount && <Row label={t({ fr: 'Montant', en: 'Amount' })} value={`${amount} ${currency}`} />}
            {orderId && <Row label={t({ fr: 'Référence', en: 'Reference' })} value={orderId} mono />}
            {paymentId && <Row label={t({ fr: 'Paiement', en: 'Payment' })} value={paymentId} mono />}
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 bg-[#1A2F4A] hover:bg-[#243a58] text-white px-6 py-3 rounded-full text-sm font-semibold transition-all"
              data-testid="success-dashboard-cta">
              {t({ fr: 'Aller à mon espace', en: 'Go to my dashboard' })}
              <ChevronRight className="w-4 h-4" />
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 text-[12px] text-[#6B4E37] hover:text-[#D4AF37] transition-colors"
              data-testid="success-back-pricing">
              <Receipt className="w-3 h-3" />
              {t({ fr: 'Retour aux tarifs', en: 'Back to pricing' })}
            </Link>
          </div>

          <p className="mt-5 text-[10px] text-[#999] italic">
            {t({ fr: `Un reçu ${product ? `pour ${product} ` : ''}a été enregistré dans votre historique.`, en: `A receipt${product ? ` for ${product}` : ''} has been saved in your history.` })}
          </p>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex items-center justify-between gap-3 text-[12px]">
      <span className="text-[#6B4E37]">{label}</span>
      <span className={`font-semibold text-[#1A2F4A] ${mono ? 'font-mono text-[11px]' : ''}`}>{value}</span>
    </div>
  );
}

export function PaymentFailure() {
  const t = useT();
  const [params] = useSearchParams();
  const err = params.get('error') || '';
  return (
    <div className="min-h-screen bg-[#FAF7F0] flex items-center justify-center p-6" data-testid="payment-failure-page">
      <div className="w-full max-w-lg bg-white border border-red-200 rounded-2xl px-8 py-9 text-center">
        <div className="w-16 h-16 rounded-full bg-red-50 border border-red-200 flex items-center justify-center mx-auto mb-5">
          <XCircle className="w-9 h-9 text-red-600" />
        </div>
        <h1 className="text-3xl font-bold text-[#1A2F4A] mb-2" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="failure-title">
          {t({ fr: 'Paiement non abouti', en: 'Payment not completed' })}
        </h1>
        <p className="text-[14px] text-[#6B4E37] mb-4">
          {t({
            fr: "Aucune somme n'a été prélevée. Vous pouvez réessayer en toute sécurité.",
            en: 'No amount has been charged. You can safely try again.',
          })}
        </p>
        {err && <p className="text-[12px] text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-5" data-testid="failure-reason">{err}</p>}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2 bg-[#1A2F4A] hover:bg-[#243a58] text-white px-6 py-3 rounded-full text-sm font-semibold transition-all"
            data-testid="failure-retry-cta">
            <RefreshCw className="w-4 h-4" />
            {t({ fr: 'Réessayer', en: 'Try again' })}
          </Link>
          <Link to="/contact" className="text-[12px] text-[#6B4E37] hover:text-[#D4AF37] transition-colors" data-testid="failure-support-link">
            {t({ fr: 'Contacter le support', en: 'Contact support' })}
          </Link>
        </div>
      </div>
    </div>
  );
}
